import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSend, FiPackage, FiCheck, FiClock,
  FiAlertCircle, FiEye, FiEdit, FiX, FiGrid, FiList, FiFileText, FiDownload,
  FiCalendar, FiDollarSign, FiColumns, FiFilter
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useCompras } from '../../hooks/useCompras';
import { useDebounce } from '../../hooks/useDebounce';
import { exportToCsv } from '../../utils/exportReport';

const statusConfig = {
  DRAFT: { 
    label: 'Borrador', 
    color: 'gray', 
    icon: FiEdit,
    actions: ['edit', 'send', 'delete']
  },
  PENDING: { 
    label: 'Pendiente', 
    color: 'yellow', 
    icon: FiClock,
    actions: ['edit', 'send', 'cancel']
  },
  SENT: { 
    label: 'Enviada', 
    color: 'blue', 
    icon: FiSend,
    actions: ['view', 'approve', 'cancel']
  },
  CONFIRMED: { 
    label: 'Confirmada', 
    color: 'green', 
    icon: FiCheck,
    actions: ['view', 'receive']
  },
  PARTIAL_RECEIVED: { 
    label: 'Parcialmente Recibida', 
    color: 'orange', 
    icon: FiPackage,
    actions: ['view', 'receive']
  },
  RECEIVED: { 
    label: 'Recibida', 
    color: 'green', 
    icon: FiCheck,
    actions: ['view']
  },
  CANCELLED: { 
    label: 'Cancelada', 
    color: 'red', 
    icon: FiX,
    actions: ['view']
  }
};

const priorityConfig = {
  LOW: { label: 'Baja', color: 'gray' },
  NORMAL: { label: 'Normal', color: 'blue' },
  HIGH: { label: 'Alta', color: 'orange' },
  URGENT: { label: 'Urgente', color: 'red' }
};

const Compras = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Refs para el popover de filtro y la onda SVG del header (igual que Clientes)
  const filterDropdownRef = useRef(null);
  const menuRef    = useRef(null);
  const sec1Ref    = useRef(null);
  const sec2Ref    = useRef(null);
  const sec3Ref    = useRef(null);
  const sec4Ref    = useRef(null);
  const svgPathRef = useRef(null);

  // Aplicar debounce al searchQuery local (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);
  
  const {
    ordenes,
    isLoading,
    error,
    searchQuery,
    statusFilter,
    buscarOrdenes,
    setStatusFilter,
    setProveedorFilter: setProveedorFilterHook,
    enviarOrden,
    aprobarOrden,
    cancelarOrden,
    getEstadisticas,
    clearError,
    limpiarBusqueda
  } = useCompras();

  // Efecto para búsqueda con debounce
  useEffect(() => {
    buscarOrdenes(debouncedSearchQuery);
  }, [debouncedSearchQuery, buscarOrdenes]);

  // ── Onda SVG del header (misma que en Clientes) ──
  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec1Ref.current || !sec2Ref.current || !sec3Ref.current || !sec4Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;
    const VB = 1000, TOP = 3, BOT = 53, half = 18, scale = VB / W;
    const b1 = (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const d = [
      `M 0,${TOP}`,
      `L ${b1 - half},${TOP}`,
      `C ${b1},${TOP} ${b1},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${b3 - half},${TOP}`,
      `C ${b3},${TOP} ${b3},${BOT} ${b3 + half},${BOT}`,
      `L ${VB},${BOT}`,
    ].join(' ');
    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    buildWavePath();
    const t = setTimeout(buildWavePath, 100);
    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref, sec4Ref].forEach(r => { if (r.current) ro.observe(r.current); });
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [buildWavePath, isLoading]);

  // Cerrar el popover de filtro al hacer clic fuera
  useEffect(() => {
    const onClick = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Efecto para cambiar de tab
  useEffect(() => {
    if (activeTab === 'todas') {
      setStatusFilter('');
    } else if (activeTab === 'borradores') {
      setStatusFilter('DRAFT');
    } else if (activeTab === 'enviadas') {
      setStatusFilter('SENT');
    } else if (activeTab === 'confirmadas') {
      setStatusFilter('CONFIRMED');
    } else if (activeTab === 'recibidas') {
      setStatusFilter('RECEIVED');
    }
  }, [activeTab, setStatusFilter]);

  const stats = getEstadisticas();

  const getOrdenesFiltradas = () => {
    if (activeTab === 'todas') return ordenes;
    if (activeTab === 'borradores') return ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING');
    if (activeTab === 'enviadas') return ordenes.filter(o => o.status === 'SENT');
    if (activeTab === 'confirmadas') return ordenes.filter(o => o.status === 'CONFIRMED');
    if (activeTab === 'recibidas') return ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED');
    return ordenes;
  };

  const ordenesFiltradas = getOrdenesFiltradas();

  const getTotalOrdenes = (tipo) => {
    switch (tipo) {
      case 'borradores':
        return ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING').length;
      case 'enviadas':
        return ordenes.filter(o => o.status === 'SENT').length;
      case 'confirmadas':
        return ordenes.filter(o => o.status === 'CONFIRMED').length;
      case 'recibidas':
        return ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED').length;
      default:
        return ordenes.length;
    }
  };

  const getTotalMonto = (tipo) => {
    let ordenesFiltradas = ordenes;
    switch (tipo) {
      case 'borradores':
        ordenesFiltradas = ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING');
        break;
      case 'enviadas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'SENT');
        break;
      case 'confirmadas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'CONFIRMED');
        break;
      case 'recibidas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED');
        break;
      default:
        ordenesFiltradas = ordenes;
    }
    return ordenesFiltradas.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  };

  const handleCreateNew = () => {
    navigate('/system/compras/nueva');
  };

  const handleDownload = () => {
    // Exporta a CSV el listado visible (respeta la pestaña/filtro activos).
    const lista = getOrdenesFiltradas();
    if (lista.length === 0) {
      alert('No hay órdenes de compra para descargar.');
      return;
    }
    const columns = [
      { label: 'N° Orden', value: (o) => o.order_number },
      { label: 'Proveedor', value: (o) => o.supplier_name },
      { label: 'Fecha', value: (o) => (o.order_date ? String(o.order_date).split('T')[0] : '') },
      { label: 'Entrega esperada', value: (o) => (o.expected_delivery_date ? String(o.expected_delivery_date).split('T')[0] : '') },
      { label: 'Estado', value: (o) => (statusConfig[o.status]?.label || o.status) },
      { label: 'Prioridad', value: (o) => (priorityConfig[o.priority]?.label || o.priority) },
      { label: 'Items', value: (o) => o.items_count },
      { label: 'Subtotal', value: (o) => o.subtotal },
      { label: 'Envío', value: (o) => o.shipping_cost },
      { label: 'Total', value: (o) => o.total_amount }
    ];
    const stamp = new Date().toISOString().split('T')[0];
    exportToCsv(columns, lista, `ordenes_compra_${stamp}`);
  };

  const handleClearSearch = () => {
    setLocalSearchQuery('');
    limpiarBusqueda();
  };

  const handleSearchChange = (e) => {
    setLocalSearchQuery(e.target.value);
  };

  const handleProveedorFilterChange = (value) => {
    setProveedorFilterHook(value);
  };

  // Limpiar errores
  const handleClearError = () => {
    clearError();
  };

  const handleViewOrder = (order) => {
    navigate(`/system/compras/${order.id}`);
  };

  const handleEditOrder = (order) => {
    navigate(`/system/compras/${order.id}/editar`);
  };

  const handleReceiveOrder = (order) => {
    navigate(`/system/compras/${order.id}/recibir`);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getThumbnailType = (status) => {
    // Todos los thumbnails en morado corporativo
    return 'blue-dark-gradient';
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.DRAFT;

    const badgeClass = {
      'gray': 'status-badge--secondary',
      'yellow': 'status-badge--warning',
      'blue': 'status-badge--info',
      'green': 'status-badge--success',
      'orange': 'status-badge--warning',
      'red': 'status-badge--danger'
    };

    return (
      <span className={`status-badge ${badgeClass[config.color] || 'status-badge--info'}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = priorityConfig[priority] || priorityConfig.NORMAL;
    
    return (
      <span className={`priority-badge priority-badge--${config.color}`}>
        {config.label}
      </span>
    );
  };

  const renderOrderActions = (order) => {
    const config = statusConfig[order.status] || statusConfig.DRAFT;
    
    return (
      <div className="content-card__actions">
        {config.actions.includes('view') && (
          <button 
            className="btn btn--ghost btn--sm"
            onClick={() => handleViewOrder(order)}
            title="Ver orden"
          >
            <FiEye />
          </button>
        )}
        
        {config.actions.includes('edit') && (
          <button 
            className="btn btn--secondary btn--sm"
            onClick={() => handleEditOrder(order)}
            title="Editar orden"
          >
            <FiEdit />
          </button>
        )}
        
        {config.actions.includes('send') && (
          <button 
            className="btn btn--primary btn--sm"
            onClick={() => enviarOrden(order.id)}
            title="Enviar orden"
          >
            <FiSend />
          </button>
        )}
        
        {config.actions.includes('approve') && (
          <button 
            className="btn btn--success btn--sm"
            onClick={() => aprobarOrden(order.id)}
            title="Aprobar orden"
          >
            <FiCheck />
          </button>
        )}
        
        {config.actions.includes('receive') && (
          <button 
            className="btn btn--info btn--sm"
            onClick={() => handleReceiveOrder(order)}
            title="Recibir productos"
          >
            <FiPackage />
          </button>
        )}
        
        {config.actions.includes('cancel') && (
          <button 
            className="btn btn--danger btn--sm"
            onClick={() => cancelarOrden(order.id)}
            title="Cancelar orden"
          >
            <FiX />
          </button>
        )}
      </div>
    );
  };

  // Solo mostrar el loader de pantalla completa en la carga inicial. Durante la
  // búsqueda/filtrado no se reemplaza toda la página (si no, el input de búsqueda
  // se desmonta y pierde el foco al escribir).
  if (isLoading && ordenes.length === 0) {
    return <LoadingGrid message="Cargando órdenes de compra..." />;
  }

  return (
    <div className="compras-page">
      {/* Error Alert */}
      {error && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">ÓRDENES DE COMPRA</h1>

      {/* Menú de acciones (misma estructura que Clientes) */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          <svg className="action-bar-wave" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 56" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="waveGradCompras" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path ref={svgPathRef} d="" fill="none" stroke="url(#waveGradCompras)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <button className="menu-item menu-item--first" ref={sec1Ref} onClick={handleCreateNew}>
            <span className="add-btn-plus">+</span>
            <span className="add-btn-label">NUEVA ORDEN</span>
          </button>

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar orden..."
              value={localSearchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="menu-item menu-item--views" ref={sec3Ref}>
            <button className={`view-btn ${viewMode === 'grid'  ? 'active' : ''}`} onClick={() => setViewMode('grid')}  title="Vista de tarjetas"><FiGrid /></button>
            <button className={`view-btn ${viewMode === 'list'  ? 'active' : ''}`} onClick={() => setViewMode('list')}  title="Vista de lista"><FiList /></button>
            <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Vista de tabla"><FiColumns /></button>
          </div>

          <div className="menu-item menu-item--checkbox" ref={sec4Ref}>
            <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
              <button
                className={`view-btn ${statusFilter ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(prev => !prev)}
                title="Filtrar por estado"
              >
                <FiFilter />
              </button>
              {showFilterDropdown && (
                <div className="filter-popover">
                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px' }}>ESTADO</div>
                  <button
                    className={`filter-popover-item ${statusFilter === '' ? 'active' : ''}`}
                    onClick={() => { setStatusFilter(''); setShowFilterDropdown(false); }}
                  >
                    {statusFilter === '' && <FiCheck />} Todos los estados
                  </button>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      className={`filter-popover-item ${statusFilter === key ? 'active' : ''}`}
                      onClick={() => { setStatusFilter(key); setShowFilterDropdown(false); }}
                    >
                      {statusFilter === key && <FiCheck />} {config.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="view-btn" onClick={handleDownload} title="Descargar lista"><FiDownload /></button>
          </div>
        </div>
      </div>

      {/* Tabs de filtro */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${activeTab === 'todas' ? 'active' : ''}`}
          onClick={() => setActiveTab('todas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('todas')}</span>
            <span className="tab-label">Todas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('todas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'borradores' ? 'active' : ''}`}
          onClick={() => setActiveTab('borradores')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('borradores')}</span>
            <span className="tab-label">Borradores</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('borradores'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'enviadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('enviadas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('enviadas')}</span>
            <span className="tab-label">Enviadas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('enviadas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'confirmadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('confirmadas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('confirmadas')}</span>
            <span className="tab-label">Confirmadas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('confirmadas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'recibidas' ? 'active' : ''}`}
          onClick={() => setActiveTab('recibidas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('recibidas')}</span>
            <span className="tab-label">Recibidas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('recibidas'))}</div>
        </button>
      </div>

      {/* Contenido */}
      {ordenesFiltradas.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {localSearchQuery ? 'No se encontraron órdenes de compra' : 'No hay órdenes de compra registradas'}
          </h3>
          <p className="empty-state-description">
            {localSearchQuery
              ? `No se encontraron órdenes que coincidan con "${localSearchQuery}"`
              : 'Comienza creando tu primera orden de compra'
            }
          </p>
          {localSearchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--compras">
            <thead>
              <tr>
                <th>N° ORDEN</th>
                <th>PROVEEDOR</th>
                <th>FECHA</th>
                <th className="text-center">ESTADO</th>
                <th className="text-center">PRIORIDAD</th>
                <th className="text-center">MONTO TOTAL</th>
                <th className="text-center">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {ordenesFiltradas.map((orden) => (
                <tr
                  key={orden.id}
                  onClick={() => handleViewOrder(orden)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><strong>{orden.order_number || `ORD-${orden.id}`}</strong></td>
                  <td>{orden.supplier_name || 'Sin proveedor'}</td>
                  <td>{formatDate(orden.order_date)}</td>
                  <td className="text-center">{getStatusBadge(orden.status)}</td>
                  <td className="text-center">{getPriorityBadge(orden.priority)}</td>
                  <td className="text-center">
                    <strong>{formatCurrency(orden.total_amount)}</strong>
                  </td>
                  <td className="text-center">
                    <div className="table-actions">
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewOrder(orden);
                        }}
                        title="Ver orden"
                      >
                        <FiEye />
                      </button>
                      {statusConfig[orden.status]?.actions.includes('edit') && (
                        <button
                          className="btn btn--secondary btn--sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOrder(orden);
                          }}
                          title="Editar"
                        >
                          <FiEdit />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {ordenesFiltradas.map((orden) => (
            <div
              key={orden.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => handleViewOrder(orden)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(orden.status)}`}>
                    <span className="logo-text">
                      {orden.order_number || `ORD-${orden.id}`}
                      <br/>
                      {formatCurrency(orden.total_amount)}
                    </span>
                  </div>
                  <div className="content-info">
                    <div className="content-title">{orden.supplier_name || 'Sin proveedor'}</div>
                    <div className="content-subtitle">
                      {formatDate(orden.order_date)} • {statusConfig[orden.status]?.label || 'N/A'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(orden.status)}`}>
                    <FiFileText className="thumbnail-icon" />
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{orden.order_number || `ORD-${orden.id}`}</div>
                    <div className="content-subtitle">{orden.supplier_name || 'Sin proveedor'}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiCalendar size={12} /> {formatDate(orden.order_date)}
                      </span>
                      <span className="meta-item">
                        <FiDollarSign size={12} /> {formatCurrency(orden.total_amount)}
                      </span>
                      {getStatusBadge(orden.status)}
                      {orden.priority !== 'NORMAL' && (
                        <span className="meta-item meta-item--warning">
                          {priorityConfig[orden.priority]?.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="content-actions-list">
                    <FiPackage className="card-icon" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Compras;
